import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import DataTable, { type TableColumn } from "react-data-table-component";
import { TablePager } from "../../components/shared/TablePager";
import { adminTableTheme } from "../../components/admin/adminTableTheme";
import { AdminGate } from "../../components/admin/AdminGate";
import { Tabs } from "../../components/admin/Tabs";
import { EmptyState, ErrorState, LoadingState } from "../../components/states";
import { useToast } from "../../components/toast/toastContext";
import styles from "../../components/admin/Admin.module.css";
import { createSeatCost, fetchProviderOptions, fetchSeatCosts, type ProviderOption, type SeatCost } from "../../api/entitlementAdmin";
import { SyncStatusContent } from "./SyncStatusPage";
import { ProviderAccessContent } from "./ProviderAccessPage";
import { BedrockModelsContent } from "./BedrockModelsPage";
import { BedrockPricesContent } from "./BedrockPricesPage";
import { WriteSafetyContent } from "./WriteSafetyPage";
import { Dropdown } from "../../components/shared/Dropdown";
import { useSession } from "../../session/SessionContext";
import type { TabDefinition } from "../../components/admin/Tabs";

const ProvidersAdministerPermission = "providers.administer";
const BedrockModelsManagePermission = "bedrock.models.manage";
const SettingsWritePermission = "settings.write";

/** The providers' tab ids, in the order they are shown. Each tab's id is its address: `/admin/providers/<id>`. */
const providerTabIds = ["sync-status", "write-safety", "seat-costs", "provider-access", "bedrock-models", "bedrock-prices"] as const;
type ProviderTabId = (typeof providerTabIds)[number];
const defaultTabId = providerTabIds[0];

/** A Providers tab, with the permission that opens it. A tab is shown only to a person who holds that permission. */
type ProviderTab = TabDefinition & { id: ProviderTabId; permission: string };

/**
 * The providers (vendors) administration screen. Seat costs are a property of the provider rather
 * than of any entitlement group or person, so they live here, next to the vendor sync status, which
 * is also per provider. Provider access and the Bedrock models are per-provider screens too. Write safety (the kill switch and the
 * change bound) sits beside the sync status because it governs every provider's writes. A person
 * sees only the tabs whose permission they hold; an address for a tab they cannot open goes to the first tab they can.
 */
export function ProvidersPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tab } = useParams<{ tab: string }>();
  const { snapshot } = useSession();

  const held = snapshot.status === "signed-in" ? snapshot.permissions : [];
  const tabs: ProviderTab[] = [
    { id: "sync-status", permission: ProvidersAdministerPermission, label: t("admin.system.tabs.syncStatus"), content: <SyncStatusContent /> },
    { id: "write-safety", permission: SettingsWritePermission, label: t("admin.system.tabs.writeSafety"), content: <WriteSafetyContent /> },
    { id: "seat-costs", permission: ProvidersAdministerPermission, label: t("admin.entitlement.seatCosts.title"), content: <SeatCostsSection /> },
    { id: "provider-access", permission: ProvidersAdministerPermission, label: t("nav.adminProviderAccess"), content: <ProviderAccessContent /> },
    { id: "bedrock-models", permission: BedrockModelsManagePermission, label: t("nav.adminBedrockModels"), content: <BedrockModelsContent /> },
    { id: "bedrock-prices", permission: SettingsWritePermission, label: t("nav.adminBedrockPrices"), content: <BedrockPricesContent /> },
  ];

  const permittedTabs = tabs.filter((definition) => held.includes(definition.permission));
  const permittedIds: readonly string[] = permittedTabs.map((definition) => definition.id);
  const isUnknownTab = tab === undefined || !(providerTabIds as readonly string[]).includes(tab);

  // A person with none of the tab permissions has no tab to go to; the gate below shows them the denied state.
  if (isUnknownTab || (permittedTabs.length > 0 && !permittedIds.includes(tab))) {
    return <Navigate to={`/admin/providers/${permittedTabs[0]?.id ?? defaultTabId}`} replace />;
  }

  return (
    <div>
      <AdminGate permissions={tabs.map((definition) => definition.permission)} mode="any" title={t("admin.entitlement.denied.title")} description={t("admin.entitlement.denied.description")}>
        <Tabs tabs={permittedTabs} activeId={tab} onSelect={(id) => void navigate(`/admin/providers/${id}`)} />
      </AdminGate>
    </div>
  );
}

/**
 * The seat costs screen. Seat costs are immutable: the table only displays them, and the one action is to add a new seat cost at the top,
 * for a provider chosen from the list of known providers. A change in cost is recorded as a new seat cost with a later effective date.
 * Outcomes are shown as toasts.
 */
function SeatCostsSection(): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded"; costs: SeatCost[] } | { kind: "error"; message: string }>({ kind: "loading" });
  const [providers, setProviders] = useState<ProviderOption[]>([]);
  const [form, setForm] = useState({ vendorId: "", amount: "0", currency: "USD", effectiveFrom: new Date().toISOString().slice(0, 10) });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const [costs, options] = await Promise.all([fetchSeatCosts(), fetchProviderOptions()]);
    if (costs.kind === "ok") {
      setState({ kind: "loaded", costs: costs.response });
    } else if (costs.kind === "denied") {
      setState({ kind: "error", message: t("admin.entitlement.denied.description") });
    } else {
      setState({ kind: "error", message: costs.message });
    }
    if (options.kind === "ok") {
      setProviders(options.response);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const providerName = useMemo(() => new Map(providers.map((provider) => [provider.vendorId, provider.displayName])), [providers]);

  const handleCreate = async () => {
    const result = await createSeatCost(form.vendorId, Number(form.amount), form.currency, new Date(form.effectiveFrom).toISOString());
    if (result.kind === "ok") {
      toast.show(t("admin.entitlement.seatCosts.added"), "success");
      void load();
    } else {
      toast.show(result.kind === "denied" ? t("admin.denied.description", { permissions: ProvidersAdministerPermission }) : result.message, "error");
    }
  };

  const columns: TableColumn<SeatCost>[] = [
    { id: "vendor", name: t("admin.entitlement.seatCosts.vendor"), selector: (row) => providerName.get(row.vendorId) ?? row.vendorId, sortable: true },
    {
      id: "amount",
      name: t("admin.entitlement.seatCosts.amount"),
      selector: (row) => row.amount,
      sortable: true,
      right: true,
      cell: (row) => row.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 }),
    },
    { id: "currency", name: t("admin.entitlement.seatCosts.currency"), selector: (row) => row.currency, sortable: true },
    {
      id: "effectiveFrom",
      name: t("admin.entitlement.seatCosts.effectiveFrom"),
      selector: (row) => row.effectiveFrom,
      sortable: true,
      cell: (row) => row.effectiveFrom.slice(0, 10),
    },
  ];

  if (state.kind === "loading") {
    return <LoadingState label={t("admin.entitlement.seatCosts.loading")} />;
  }

  if (state.kind === "error") {
    return <ErrorState title={t("admin.entitlement.seatCosts.errorTitle")} description={state.message} onRetry={() => void load()} />;
  }

  return (
    <>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          void handleCreate();
        }}
      >
        <div className={styles.field}>
          <label htmlFor="new-seat-vendor">{t("admin.entitlement.seatCosts.vendor")}</label>
          <Dropdown
            id="new-seat-vendor"
            options={[
              { value: "", label: t("admin.entitlement.seatCosts.chooseProvider") },
              ...providers.map((provider) => ({ value: provider.vendorId, label: provider.displayName })),
            ]}
            value={form.vendorId}
            onChange={(vendorId) => setForm({ ...form, vendorId })}
          />
        </div>
        <div className={styles.field}>
          <label htmlFor="new-seat-amount">{t("admin.entitlement.seatCosts.amount")}</label>
          <input id="new-seat-amount" type="number" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} />
        </div>
        <div className={styles.field}>
          <label htmlFor="new-seat-currency">{t("admin.entitlement.seatCosts.currency")}</label>
          <input id="new-seat-currency" value={form.currency} onChange={(event) => setForm({ ...form, currency: event.target.value })} />
        </div>
        <div className={styles.field}>
          <label htmlFor="new-seat-effective">{t("admin.entitlement.seatCosts.effectiveFrom")}</label>
          <input id="new-seat-effective" type="date" value={form.effectiveFrom} onChange={(event) => setForm({ ...form, effectiveFrom: event.target.value })} />
        </div>
        <button type="submit" className="button button-primary" disabled={!form.vendorId}>
          {t("admin.entitlement.seatCosts.create")}
        </button>
      </form>
      {state.costs.length === 0 ? (
        <EmptyState title={t("admin.entitlement.seatCosts.title")} description={t("admin.entitlement.seatCosts.empty")} />
      ) : (
        <div className={`${styles.tableWrapper} ${styles.peopleTableWrapper}`}>
          <DataTable
            columns={columns}
            data={state.costs}
            className={styles.peopleDataTable}
            keyField="id"
            ariaLabel={t("admin.entitlement.seatCosts.title")}
            theme={adminTableTheme}
            defaultSortFieldId="vendor"
            pagination
            paginationComponent={TablePager}
            paginationPerPage={25}
            paginationRowsPerPageOptions={[25, 50, 100]}
            striped
            highlightOnHover
            columnSeparator="subtle"
          />
        </div>
      )}
    </>
  );
}
